// useNotificationPosition — calculates fixed-position coordinates for the
// notification panel based on the bell button's bounding rect.
//
// Sidebar variant: panel opens to the right of the bell, bottom-aligned
// (panel's bottom-left corner touches the bell).
//
// Header variant: panel opens below the bell, right-aligned.
//
// Recalculates on scroll + resize, throttled via requestAnimationFrame.

import { useEffect, useRef, useState, type RefObject } from 'react';

export interface PanelPosition {
  /** Only set for header variant (panel opens below bell). */
  top?: number;
  /** Only set for sidebar variant (panel bottom aligns with bell bottom). */
  bottom?: number;
  left: number;
  /** Preferred max-height so the panel doesn't overflow the viewport. */
  maxHeight: number;
}

/** Gap between bell button and panel, in px. */
const GAP = 6;
/** Minimum distance from viewport edge, in px. */
const VIEWPORT_PAD = 16;
/** Panel width, in px — must match .notif-panel width in CSS. */
const PANEL_W = 380;

export function useNotificationPosition(
  btnRef: RefObject<HTMLElement>,
  open: boolean,
  variant: 'header' | 'sidebar',
): PanelPosition | null {
  const [pos, setPos] = useState<PanelPosition | null>(null);
  const rafId = useRef<number>(0);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- panel position is derived from getBoundingClientRect on open + scroll/resize subscription; setState is the output of DOM measurement (this branch: close-reset)
      setPos(null);
      return;
    }

    function recalc() {
      const btn = btnRef.current;
      if (!btn) return;

      const r = btn.getBoundingClientRect();
      const vh = window.innerHeight;

      if (variant === 'sidebar') {
        // Panel opens to the right of the bell, bottom-aligned.
        // bottom = distance from panel bottom edge to viewport bottom.
        const bottom = vh - r.bottom;
        const left = r.right + GAP;
        const maxHeight = Math.min(480, vh - VIEWPORT_PAD * 2);
        setPos({ bottom, left, maxHeight });
      } else {
        // Header variant: panel opens below the bell, right-aligned.
        const top = r.bottom + GAP;
        const maxHeight = Math.min(480, vh - top - VIEWPORT_PAD);
        const left = Math.max(VIEWPORT_PAD, r.right - PANEL_W);
        setPos({ top, left, maxHeight });
      }
    }

    recalc();

    function onScroll() {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(recalc);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [open, btnRef, variant]);

  return pos;
}
